import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { AccordionItem, Row, Col } from 'reactstrap';
import paginationFactory, { PaginationProvider } from 'react-bootstrap-table2-paginator';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import 'boxicons';

import {
  AccordionTitle,
  AccordionTable,
  BadgeNumberStyled,
  BadgeTypeStyled,
  UncontrolledAccordionStyled,
  TableWrapper,
} from './styles';

import {
  statusColorClass,
  statusNumber,
  guidelineDeliveryTypeOptionsKanban,
} from '../../../../constants';
import { getPageOptions } from 'pages/tasks/config/tableConfig';
import { getGuidelineColumns } from 'pages/guideline/config/getGuidelineColumns';
import TitleGuideline from '../Title-Guideline';
import GuidelineContainerDatePicker from '../Guideline-Container-Date-Picker';
import GuidelineContainerUrlInput from '../Guideline-Container-Url-Input';
import MenuDropdown from '../Kanban-Card-Guideline/components/Menu-Dropdown';

const AccordionList = ({ column, data }) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const [guidelines, setGuidelines] = useState([]);

  const statusElem = (meetingStatus) => (
    <span className={`status ${statusColorClass[meetingStatus]} badge rounded-pill font-size-12`}>
      {translate(statusNumber[meetingStatus])}
    </span>
  );

  const publishedDateElem = (dateDelivery, id) => (
    <GuidelineContainerDatePicker date={dateDelivery} id={id} anyDate={true} />
  );

  const actionButtons = (id) => {
    const actionElem = <MenuDropdown guidelineId={id} />;

    return actionElem;
  };

  useEffect(() => {
    const guidelinesData = data.cards ?? [];

    const newData = guidelinesData.map((guideline) => {
      return {
        position: guideline.position,
        deliveryType: guideline.deliveryType ? (
          <div className="d-flex">
            {guideline?.count && (
              <BadgeNumberStyled className="badge-number">{`${guideline.count < 10 ? 0 : ''}${guideline.count}`}</BadgeNumberStyled>
            )}
            <BadgeTypeStyled $type={guideline.deliveryType}>
              {guidelineDeliveryTypeOptionsKanban
                .find((type) => type.value === guideline.deliveryType)
                ?.label.toUpperCase()}
            </BadgeTypeStyled>
          </div>
        ) : null,
        title: (
          <div
            onClick={() => {
              sessionStorage.setItem('lastVisitedPath', window.location.pathname);
              navigate(`/old-guideline/${guideline.id}`);
            }}
          >
            <TitleGuideline guideline={guideline} short={true} />
          </div>
        ),
        status: statusElem(guideline.status),
        statusValue: guideline.status,
        publishedDate: publishedDateElem(guideline.publishedDate, guideline.id),
        publishedValue: guideline.publishedDate ?? 0,
        publishedUrl: <GuidelineContainerUrlInput url={guideline.publishedUrl} id={guideline.id} />,
        action: actionButtons(guideline.id),
      };
    });

    setGuidelines(newData);
  }, [data]);

  return (
    <UncontrolledAccordionStyled
      className="mb-4 overflow-visible"
      defaultOpen={['1', '2', '3', '4', '5']}
      stayOpen
    >
      <AccordionItem>
        <AccordionTitle targetId="1" className="p-0 border-0">
          {`${translate(column)} (${data?.cards?.length ?? 0})`}
        </AccordionTitle>

        <AccordionTable accordionId="1">
          <Row>
            <Col lg="12" className="overflow-visible">
              <PaginationProvider
                pagination={paginationFactory(getPageOptions(guidelines.length))}
                keyField="id"
                columns={getGuidelineColumns(translate)}
                data={guidelines}
              >
                {({ paginationTableProps }) => (
                  <ToolkitProvider
                    keyField="id"
                    columns={getGuidelineColumns(translate)}
                    data={guidelines}
                  >
                    {(toolkitProps) => (
                      <React.Fragment>
                        <Row>
                          <Col xl="12">
                            <TableWrapper className="table-responsive overflow-visible">
                              <BootstrapTable
                                keyField="id"
                                responsive
                                bordered={false}
                                striped={false}
                                classes="table align-middle table-nowrap"
                                {...toolkitProps.baseProps}
                                {...paginationTableProps}
                              />
                            </TableWrapper>
                          </Col>
                        </Row>
                      </React.Fragment>
                    )}
                  </ToolkitProvider>
                )}
              </PaginationProvider>
            </Col>
          </Row>
        </AccordionTable>
      </AccordionItem>
    </UncontrolledAccordionStyled>
  );
};

AccordionList.displayName = 'Accordion List Component';

AccordionList.propTypes = {
  column: PropTypes.string,
  data: PropTypes.array,
};

const arePropsEqual = (prevProps, nextProps) => {
  const isColumnEquals = prevProps.column === nextProps.column;
  const isDataEquals = JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);

  return isColumnEquals && isDataEquals;
};

export default memo(AccordionList, arePropsEqual);
